import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import type { EditorCommand, ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { getStepRange } from '@atlaskit/editor-common/utils';
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { convertMarkdownToProsemirror } from '@atlassian/ai-model-io/convert-markdown-to-prosemirror';
import type { EditorInsertContentCommandProperties } from '@atlassian/rovo-content-bridge-api-commands/commands/editor-insert-content-command';

import type { AIPlugin } from '../aiPluginType';
import type { InsertedContentNodeTransformer } from '../editor-actions/types';
import {
	beforeDispatchAIRovoAgentTransaction,
	insertAIContentAtCurrentPosition,
	replaceWithAIContent,
} from '../ui/prebuilt/config-items/utils/action-utils';
import type { Positions } from '../ui/utils/xstate/get-ai-experience-service';

/**
 * Inserts or replaces content in the editor based on the bridge API command payload.
 *
 * Handles `placement` values:
 * - `'at-cursor'` — inserts at the current cursor position
 * - `'before-block'` / `'after-block'` — inserts relative to the current block node
 * - `'replace-selection'` — replaces the current selection
 *
 * The `'upload-and-insert-media'` mode is not handled by this action.
 *
 * This is the bridge API replacement for the legacy `useSubscribe` + `editor-suggestion`
 * pub/sub pattern from `@atlaskit/rovo-triggers`.
 *
 * @remarks This should be moved to a different plugin in the future.
 */
export const insertContentCommand = ({
	editorView,
	api,
	insertedContentNodeTransformer,
	payload,
	positions,
	scrollIntoView = true,
}: {
	api?: ExtractInjectionAPI<AIPlugin> | undefined;
	editorView: EditorView;
	insertedContentNodeTransformer?: InsertedContentNodeTransformer;
	payload: EditorInsertContentCommandProperties;
	positions: Positions;
	scrollIntoView?: boolean;
}): EditorCommand => {
	return () => {
		if (payload.mode === 'upload-and-insert-media') {
			// upload-and-insert-media mode — not handled here
			return null;
		}

		const { state } = editorView;
		const pmFragment =
			payload.data.type === 'text/markdown'
				? convertMarkdownToProsemirror({
						markdown: payload.data.content,
						schema: state.schema,
						idMap: {},
				  }).pmFragment
				: // Handles text/adf
				  Fragment.fromJSON(state.schema, payload.data.content);

		if (!pmFragment) {
			return null;
		}

		let tr =
			payload.placement === 'replace-selection'
				? replaceWithAIContent({
						editorView,
						positions,
						pmFragment,
						insertedContentNodeTransformer,
						options: { replaceDocOnEmptySelection: false },
				  })
				: insertAIContentAtCurrentPosition({
						editorView,
						positions,
						pmFragment,
						insertedContentNodeTransformer,
				  });

		// Queue URLs for smartlink resolution.
		//
		// Use the transaction's step range (which already reflects all applied
		// steps, including any node transformations performed during insertion)
		// rather than `pmFragment.size`, which is captured before
		// `insertedContentNodeTransformer` may have modified node sizes.
		const stepRange = getStepRange(tr);
		const queueCardsFromRangeCommand = api?.card?.commands?.queueCardsFromRange;

		const isMauiEmbedFixKillswitchOn =
			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- dynamic config killswitch, not a standard feature gate
			FeatureGates.getExperimentValue<boolean>('maui-editor-ai-maui-embed-fix', 'value', false);
		const isMauiExperimentEnabled = expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true);
		const useScopedQueueCardsFromRange = !isMauiEmbedFixKillswitchOn && isMauiExperimentEnabled;
		if (useScopedQueueCardsFromRange && stepRange && queueCardsFromRangeCommand) {
			const updatedTr = queueCardsFromRangeCommand(
				stepRange.from,
				stepRange.to,
				INPUT_METHOD.MANUAL,
			)({ tr });
			if (updatedTr) {
				tr = updatedTr;
			}
		} else if (api?.card?.actions.queueCardsFromChangedTr) {
			// Fallback for older card plugin versions without queueCardsFromRange,
			// or when the transaction has no step range to scope to. The
			// `queueCardsFromChangedTr` action is a no-op when there are no
			// steps, so it is safe to call unconditionally here.
			tr = api.card.actions.queueCardsFromChangedTr(state, tr, INPUT_METHOD.MANUAL);
		}

		beforeDispatchAIRovoAgentTransaction(tr);

		if (scrollIntoView) {
			tr.scrollIntoView();
		} else {
			tr.setMeta('scrollIntoView', false);
		}

		// keep the user selection if user move the cursor during remixing or
		// select the inserted position so that the floating toolbar don't appear
		if (payload.placement === 'after-block' && fg('platform_editor_ai_remix_in_editor_loading')) {
			const oldBeforeNodeEndPos = positions[1] - 1;
			const oldNode = editorView.state.doc.resolve(positions[1]).nodeBefore;
			if (oldNode) {
				const oldAfterNodeOpenPos = oldBeforeNodeEndPos - oldNode.content.size;
				// get the position of the start and end of the node where Remix is invoked
				const beforeNodeEndPos = tr.mapping.map(oldBeforeNodeEndPos);
				const afterNodeOpenPos = tr.mapping.map(oldAfterNodeOpenPos);
				// get the mapped position after the inserted content
				let to = tr.mapping.map(positions[1]);
				// if the user selection is not within the node, use the mapped position of the user selection
				if (
					!(
						tr.mapping.map(editorView.state.selection.from) >= afterNodeOpenPos &&
						tr.mapping.map(editorView.state.selection.to) <= beforeNodeEndPos
					)
				) {
					to = tr.mapping.map(editorView.state.selection.to);
				}
				tr.setSelection(TextSelection.create(tr.doc, to));
			}
		}

		return tr;
	};
};
